import React, { useState, useEffect, useMemo } from "react";
import Tesseract from "tesseract.js";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

// นำเข้า Components และ Pages ที่แยกไฟล์ไว้
import Login from "./components/Login";
import InvestmentPage from "./pages/Investment";
import GoalPage from "./pages/Goals";
import PlannerPage from "./pages/Planner";
import DCAPage from "./pages/DCA";

// นำเข้า Icons
import {
  IconWallet,
  IconHome,
  IconTarget,
  IconActivity,
  IconSearch,
  IconPlus,
  IconCamera,
  IconX,
  IconRefresh,
  IconTrash,
  IconBarChart,
  IconTrending,
  IconClock,
  IconChevronDown,
  IconLogOut,
  IconFlag,
  IconCalendar,
} from "./components/icons";

// นำเข้า Constants & Helpers
import {
  ASSET_GROUPS,
  THAI_MONTHS_SHORT,
  CHART_COLORS,
} from "./config/constants";
import {
  getCurrentDateTimeLocal,
  parseDateSafe,
  formatThaiDate,
} from "./utils/helpers";

// ------------------------------------------------------------------
// 🟢 1. หมวดหมู่เริ่มต้นสำหรับ App (เปลี่ยนเป็นรูปแบบ Emoji เพื่อให้แก้ไขได้)
// ------------------------------------------------------------------
const DEFAULT_APP_CATEGORIES = {
  Expense: [
    { id: "e1", label: "อาหารและเครื่องดื่ม", emoji: "🍔", color: "#F6AD55" },
    { id: "e2", label: "ช้อปปิ้ง", emoji: "🛍️", color: "#F687B3" },
    { id: "e3", label: "ค่าเดินทาง", emoji: "🚕", color: "#F6E05E" },
    { id: "e4", label: "ค่าไฟค่าน้ำ", emoji: "⚡", color: "#63B3ED" },
    { id: "e5", label: "ค่าอินเตอร์เน็ต", emoji: "🌐", color: "#4FD1C5" },
    { id: "e6", label: "Youtube", emoji: "🔴", color: "#FC8181" },
    { id: "e7", label: "Netflix", emoji: "🎬", color: "#E53E3E" },
    { id: "e8", label: "ลงทุน", emoji: "📈", color: "#68D391" },
    { id: "e9", label: "ออมเงิน", emoji: "🐷", color: "#B794F4" },
    { id: "e10", label: "อื่นๆ", emoji: "📝", color: "#A0AEC0" },
  ],
  Income: [
    { id: "i1", label: "เงินเดือน", emoji: "💰", color: "#68D391" },
    { id: "i2", label: "Freelance", emoji: "💼", color: "#B794F4" },
    { id: "i3", label: "เงินรางวัล", emoji: "⭐", color: "#F6E05E" },
    { id: "i4", label: "กำไรจาก Crypto", emoji: "₿", color: "#F6AD55" },
    { id: "i5", label: "อื่นๆ", emoji: "📝", color: "#A0AEC0" },
  ],
};

// พาเลทสีสำหรับสุ่มเวลาสร้างหมวดหมู่ใหม่
const EXTENDED_COLORS = [
  "#4FD1C5",
  "#9F7AEA",
  "#F6E05E",
  "#FC8181",
  "#63B3ED",
  "#F6AD55",
  "#F687B3",
  "#34D399",
  "#A78BFA",
  "#F87171",
];

// ------------------------------------------------------------------
// 🟢 2. Component สำหรับจัดการหมวดหมู่ (เพิ่ม/ลด/แก้ ได้)
// ------------------------------------------------------------------
const CategorySelector = ({
  type,
  selectedCategory,
  onSelect,
  appCategories,
  setAppCategories,
}) => {
  const [isAddingCat, setIsAddingCat] = useState(false);
  const [newEmoji, setNewEmoji] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [editingCatId, setEditingCatId] = useState(null);
  const [editEmoji, setEditEmoji] = useState("");
  const [editLabel, setEditLabel] = useState("");

  const categories = appCategories[type] || [];

  const saveNewCategory = () => {
    if (!newLabel.trim()) return;
    const nextColor =
      EXTENDED_COLORS[categories.length % EXTENDED_COLORS.length];
    const newCat = {
      id: Date.now().toString(),
      label: newLabel.trim(),
      emoji: newEmoji.trim() || "📌",
      color: nextColor,
    };
    setAppCategories({ ...appCategories, [type]: [...categories, newCat] });
    onSelect(newCat.label);
    setIsAddingCat(false);
    setNewEmoji("");
    setNewLabel("");
  };

  const startEditCategory = (cat, e) => {
    e.stopPropagation();
    setEditingCatId(cat.id);
    setEditEmoji(cat.emoji);
    setEditLabel(cat.label);
    setIsAddingCat(false);
  };

  const saveEditCategory = (id) => {
    if (!editLabel.trim()) return;
    const oldCat = categories.find((c) => c.id === id);
    const newLabelText = editLabel.trim();
    const updatedList = categories.map((c) =>
      c.id === id
        ? { ...c, label: newLabelText, emoji: editEmoji.trim() || "📌" }
        : c
    );

    setAppCategories({ ...appCategories, [type]: updatedList });
    if (selectedCategory === oldCat.label) onSelect(newLabelText);
    setEditingCatId(null);
  };

  const handleDeleteCategory = (id, e) => {
    e.stopPropagation();
    if (window.confirm("ต้องการลบหมวดหมู่นี้ใช่ไหม?")) {
      setAppCategories({
        ...appCategories,
        [type]: categories.filter((c) => c.id !== id),
      });
    }
  };

  return (
    <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-1 scrollbar-thin">
      {categories.map((cat) => {
        const isActive = selectedCategory === cat.label;

        if (editingCatId === cat.id) {
          return (
            <div
              key={cat.id}
              className="flex items-center gap-2 px-3 py-1 rounded-full border border-yellow-500/50 bg-yellow-500/10"
            >
              <input
                type="text"
                className="w-6 bg-transparent text-center outline-none text-sm"
                value={editEmoji}
                onChange={(e) => setEditEmoji(e.target.value)}
                maxLength={2}
              />
              <div className="w-px h-4 bg-yellow-500/30"></div>
              <input
                type="text"
                className="w-20 bg-transparent outline-none text-xs font-bold text-white"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
              />
              <button
                type="button"
                onClick={() => saveEditCategory(cat.id)}
                className="text-yellow-400 hover:text-yellow-300 font-bold ml-1"
              >
                ✓
              </button>
              <button
                type="button"
                onClick={() => setEditingCatId(null)}
                className="text-slate-400 hover:text-red-400 font-bold ml-1"
              >
                ✕
              </button>
            </div>
          );
        }

        return (
          <div key={cat.id} className="relative group">
            <button
              type="button"
              onClick={() => onSelect(cat.label)}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-bold transition-all"
              style={
                isActive
                  ? {
                      borderColor: cat.color,
                      color: cat.color,
                      backgroundColor: `${cat.color}1A`,
                    }
                  : {
                      borderColor: "#1e293b",
                      color: "#94a3b8",
                      backgroundColor: "#020617",
                    }
              }
            >
              <span className="text-sm">{cat.emoji}</span>
              <span>{cat.label}</span>
            </button>
            <div className="absolute -top-2 -right-2 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-all shadow-lg rounded-full bg-slate-800 p-0.5 border border-slate-600">
              <button
                type="button"
                onClick={(e) => startEditCategory(cat, e)}
                className="text-slate-300 hover:text-white hover:bg-blue-500 rounded-full w-5 h-5 flex items-center justify-center transition-colors"
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                  <path d="m15 5 4 4" />
                </svg>
              </button>
              <button
                type="button"
                onClick={(e) => handleDeleteCategory(cat.id, e)}
                className="text-slate-300 hover:text-white hover:bg-red-500 rounded-full w-5 h-5 flex items-center justify-center text-[10px] transition-colors font-bold"
              >
                ✕
              </button>
            </div>
          </div>
        );
      })}

      {!isAddingCat ? (
        <button
          type="button"
          onClick={() => setIsAddingCat(true)}
          className="flex items-center gap-1 px-4 py-2 rounded-xl border border-dashed border-slate-700 bg-slate-900 text-slate-400 text-xs font-bold hover:text-white hover:border-slate-500 transition-all"
        >
          <IconPlus size={14} /> เพิ่ม
        </button>
      ) : (
        <div className="flex items-center gap-2 px-3 py-1 rounded-xl border border-emerald-500/50 bg-emerald-500/10">
          <input
            type="text"
            placeholder="😀"
            className="w-6 bg-transparent text-center outline-none text-sm"
            value={newEmoji}
            onChange={(e) => setNewEmoji(e.target.value)}
            maxLength={2}
          />
          <div className="w-px h-4 bg-emerald-500/30"></div>
          <input
            type="text"
            placeholder="ชื่อหมวด"
            className="w-20 bg-transparent outline-none text-xs font-bold text-white placeholder:font-normal placeholder:text-emerald-500/50"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
          />
          <button
            type="button"
            onClick={saveNewCategory}
            className="text-emerald-400 hover:text-emerald-300 font-bold ml-1"
          >
            ✓
          </button>
          <button
            type="button"
            onClick={() => setIsAddingCat(false)}
            className="text-slate-400 hover:text-red-400 font-bold ml-1"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

// ==================================================================
// MAIN APP
// ==================================================================
export default function App() {
  const [apiUrl] = useState(
    "https://script.google.com/macros/s/AKfycbwb7I0DHQVjOWbl800O9olJUGggfJTIrw7AqkdDOm4GeYUD86Yzz4Rp0nQk9tOMM2gCFg/exec"
  );

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);

  const [activeTab, setActiveTab] = useState("dashboard");
  const [assets, setAssets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [assetHistory, setAssetHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const [appCategories, setAppCategories] = useState(() => {
    const saved = localStorage.getItem("appCategories");
    return saved ? JSON.parse(saved) : DEFAULT_APP_CATEGORIES;
  });

  useEffect(() => {
    localStorage.setItem("appCategories", JSON.stringify(appCategories));
  }, [appCategories]);

  const [showRecordModal, setShowRecordModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [chartViewMode, setChartViewMode] = useState("spending");
  const [assetSubMode, setAssetSubMode] = useState("ALL");
  const [searchText, setSearchText] = useState("");
  const [filterCategory, setFilterCategory] = useState("All Categories");
  const [filterDateRange, setFilterDateRange] = useState("All Time");

  const [isProcessingImg, setIsProcessingImg] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    amount: "",
    note: "",
    type: "Expense",
    assetId: "",
    category: "",
    date: getCurrentDateTimeLocal(),
  });
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [isCreatingAsset, setIsCreatingAsset] = useState(false);
  const [newAssetForm, setNewAssetForm] = useState({
    name: "",
    value: "",
    type: "Bank",
  });
  const [editingAsset, setEditingAsset] = useState(null);

  const yearOptions = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i + 1
  );

  const allCategories = useMemo(() => {
    const expenseCats = appCategories.Expense.map((c) => c.label);
    const incomeCats = appCategories.Income.map((c) => c.label);
    return ["All Categories", ...new Set([...expenseCats, ...incomeCats])];
  }, [appCategories]);

  const getCatInfo = (catName) => {
    let cat = appCategories.Expense.find((c) => c.label === catName);
    if (cat) return cat;
    cat = appCategories.Income.find((c) => c.label === catName);
    if (cat) return cat;
    return { emoji: "📌", color: "#94A3B8" };
  };

  // -------------------------------------------------------------
  // 🟢 ฟังก์ชันจัดฟอร์แมตเวลา (พิมพ์ 1430 กลายเป็น 14:30 ให้อัตโนมัติ)
  // -------------------------------------------------------------
  const formatTimeInput = (val) => {
    let v = val.replace(/\D/g, ""); // ลบตัวอักษรที่ไม่ใช่ตัวเลขออกให้หมด
    if (v.length > 4) v = v.slice(0, 4);

    let hh = v.slice(0, 2);
    let mm = v.slice(2, 4);

    // คุมเพดานชั่วโมงไม่เกิน 23, นาทีไม่เกิน 59
    if (hh.length === 2 && parseInt(hh) > 23) hh = "23";
    if (mm.length === 2 && parseInt(mm) > 59) mm = "59";

    let res = hh;
    if (v.length >= 3) res = hh + ":" + mm;
    return res;
  };

  const handleTimeBlur = (val, currentDateTime, updateFn) => {
    let [hh, mm] = (val || "").split(":");
    hh = hh ? hh.padStart(2, "0") : "00";
    mm = mm ? mm.padStart(2, "0") : "00";
    const datePart = currentDateTime.split("T")[0];
    updateFn(`${datePart}T${hh}:${mm}`);
  };

  useEffect(() => {
    const storedAuth = localStorage.getItem("isLoggedIn");
    if (storedAuth === "true") setIsAuthenticated(true);
    setIsAuthChecking(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated) refreshData();
  }, [isAuthenticated]);

  const handleLoginSuccess = () => {
    localStorage.setItem("isLoggedIn", "true");
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("isLoggedIn");
    setIsAuthenticated(false);
  };

  const refreshData = () => {
    setLoading(true);
    const fetchData = async (action) => {
      try {
        const res = await fetch(`${apiUrl}?action=${action}`);
        const json = await res.json();
        return json.status === "success" ? json.data : [];
      } catch (e) {
        return [];
      }
    };
    Promise.all([
      fetchData("getAssets"),
      fetchData("getTransactions"),
      fetchData("getHistory"),
    ]).then(([assetsData, transData, historyData]) => {
      setAssets(assetsData);
      setTransactions(transData);
      setAssetHistory(historyData);

      if (assetsData.length > 0 && !form.assetId) {
        const defaultCat = appCategories["Expense"][0]?.label || "";
        setForm((prev) => ({
          ...prev,
          assetId: assetsData[0].ID,
          category: defaultCat,
        }));
      }
      setLoading(false);
    });
  };

  const getAssetGroupType = (type) => {
    for (const groupKey in ASSET_GROUPS) {
      if (ASSET_GROUPS[groupKey].types.includes(type))
        return ASSET_GROUPS[groupKey];
    }
    return ASSET_GROUPS.PERSONAL;
  };

  const dashboardData = useMemo(() => {
    const yearTrans = transactions.filter((t) => {
      const d = parseDateSafe(t.Date);
      return d && d.getFullYear() === selectedYear;
    });
    const monthTrans = yearTrans.filter((t) => {
      const d = parseDateSafe(t.Date);
      return d && d.getMonth() === selectedMonth;
    });
    const totalIncome = monthTrans
      .filter((t) => t.Type === "Income")
      .reduce((sum, t) => sum + parseFloat(t.Amount), 0);
    const totalExpense = monthTrans
      .filter((t) => t.Type === "Expense")
      .reduce((sum, t) => sum + parseFloat(t.Amount), 0);

    const catMap = {};
    monthTrans
      .filter((t) => t.Type === "Expense")
      .forEach((t) => {
        const cat = t.Category || "Other";
        catMap[cat] = (catMap[cat] || 0) + parseFloat(t.Amount);
      });

    const topCatName =
      Object.keys(catMap).sort((a, b) => catMap[b] - catMap[a])[0] || "-";
    const topCatAmount = catMap[topCatName] || 0;

    const spendingDonutData = Object.keys(catMap)
      .map((key) => ({
        name: key,
        value: catMap[key],
        color: getCatInfo(key).color,
      }))
      .sort((a, b) => b.value - a.value);

    const spendingTrendData = Array(12)
      .fill(0)
      .map((_, i) => ({ name: THAI_MONTHS_SHORT[i], Income: 0, Expense: 0 }));
    yearTrans.forEach((t) => {
      const d = parseDateSafe(t.Date);
      if (d) {
        if (t.Type === "Income")
          spendingTrendData[d.getMonth()].Income += parseFloat(t.Amount);
        else spendingTrendData[d.getMonth()].Expense += parseFloat(t.Amount);
      }
    });

    const assetTrendData = [];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const targetDate = new Date(
        today.getFullYear(),
        today.getMonth() - i + 1,
        0
      );
      targetDate.setHours(23, 59, 59, 999);
      let totalAsset = 0,
        totalDebt = 0;
      assets.forEach((asset) => {
        const isDebt = getAssetGroupType(asset.Type).id === "LIABILITY";
        const history = assetHistory
          .map((h) => ({ ...h, d: parseDateSafe(h.Date) }))
          .filter((h) => h.d && h.d <= targetDate && h.Asset_ID === asset.ID)
          .sort((a, b) => b.d - a.d);
        let val =
          history.length > 0
            ? parseFloat(history[0].Value)
            : i === 0
            ? parseFloat(asset.Current_Value)
            : 0;
        if (isDebt) totalDebt += val;
        else totalAsset += val;
      });
      assetTrendData.push({
        name: THAI_MONTHS_SHORT[targetDate.getMonth()],
        Assets: totalAsset,
        Liabilities: totalDebt,
        NetWorth: totalAsset - totalDebt,
      });
    }

    const assetGroupMap = {},
      liquidMap = {},
      investMap = {};
    assets
      .filter((a) => getAssetGroupType(a.Type).id !== "LIABILITY")
      .forEach((a) => {
        const group = getAssetGroupType(a.Type);
        assetGroupMap[group.title] =
          (assetGroupMap[group.title] || 0) + parseFloat(a.Current_Value);
      });
    assets
      .filter((a) => getAssetGroupType(a.Type).id === "LIQUID")
      .forEach((a) => {
        liquidMap[a.Asset_Name] =
          (liquidMap[a.Asset_Name] || 0) + parseFloat(a.Current_Value);
      });
    assets
      .filter((a) => getAssetGroupType(a.Type).id === "INVESTMENT")
      .forEach((a) => {
        investMap[a.Type] =
          (investMap[a.Type] || 0) + parseFloat(a.Current_Value);
      });

    return {
      totalExpense,
      totalIncome,
      topCatName,
      topCatAmount,
      spendingTrendData,
      spendingDonutData,
      assetTrendData,
      assetDonutData: Object.keys(assetGroupMap).map((k, i) => ({
        name: k,
        value: assetGroupMap[k],
        color: CHART_COLORS[i % CHART_COLORS.length],
      })),
      liquidDonutData: Object.keys(liquidMap).map((k, i) => ({
        name: k,
        value: liquidMap[k],
        color: CHART_COLORS[i % CHART_COLORS.length],
      })),
      investDonutData: Object.keys(investMap).map((k, i) => ({
        name: k,
        value: investMap[k],
        color: CHART_COLORS[i % CHART_COLORS.length],
      })),
    };
  }, [
    transactions,
    selectedMonth,
    selectedYear,
    assets,
    assetHistory,
    appCategories,
  ]);

  const getDisplayDonutData = () => {
    if (chartViewMode === "spending") return dashboardData.spendingDonutData;
    if (assetSubMode === "LIQUID") return dashboardData.liquidDonutData;
    if (assetSubMode === "INVESTMENT") return dashboardData.investDonutData;
    return dashboardData.assetDonutData;
  };
  const getDisplayDonutTotal = () => {
    if (chartViewMode === "spending") return dashboardData.totalExpense;
    return getDisplayDonutData().reduce((sum, item) => sum + item.value, 0);
  };

  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];
    if (searchText) {
      const lower = searchText.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          (t.Note && t.Note.toLowerCase().includes(lower)) ||
          (t.Category && t.Category.toLowerCase().includes(lower)) ||
          t.Amount.toString().includes(lower)
      );
    }
    if (filterCategory !== "All Categories")
      filtered = filtered.filter((t) => t.Category === filterCategory);
    const now = new Date();
    if (filterDateRange === "1 Day")
      filtered = filtered.filter((t) => {
        const d = parseDateSafe(t.Date);
        return d && d.toDateString() === now.toDateString();
      });
    else if (filterDateRange === "1 Week") {
      const weekAgo = new Date();
      weekAgo.setDate(now.getDate() - 7);
      filtered = filtered.filter((t) => {
        const d = parseDateSafe(t.Date);
        return d && d >= weekAgo;
      });
    } else if (filterDateRange === "This Month")
      filtered = filtered.filter((t) => {
        const d = parseDateSafe(t.Date);
        return (
          d &&
          d.getMonth() === now.getMonth() &&
          d.getFullYear() === now.getFullYear()
        );
      });
    else if (filterDateRange === "Last Month") {
      const lm = new Date();
      lm.setMonth(now.getMonth() - 1);
      filtered = filtered.filter((t) => {
        const d = parseDateSafe(t.Date);
        return (
          d &&
          d.getMonth() === lm.getMonth() &&
          d.getFullYear() === lm.getFullYear()
        );
      });
    }
    return filtered.sort(
      (a, b) => parseDateSafe(b.Date) - parseDateSafe(a.Date)
    );
  }, [transactions, searchText, filterCategory, filterDateRange]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setIsProcessingImg(true);
    setStatus("📷 กำลังอ่านสลิป...");
    try {
      const result = await Tesseract.recognize(file, "eng+tha");
      const text = result.data.text;
      const amountMatch = text.match(/[\d,]+\.\d{2}/);
      const matches = text.match(/(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})/);
      let foundDate = null;
      if (matches) {
        const day = parseInt(matches[1]);
        const month = parseInt(matches[2]);
        let year = parseInt(matches[3]);
        if (year < 100) year += 2000;
        if (year > 2400) year -= 543;
        foundDate = new Date(year, month - 1, day);
        foundDate.setHours(new Date().getHours());
      }
      setForm((prev) => ({
        ...prev,
        amount: amountMatch ? amountMatch[0].replace(/,/g, "") : prev.amount,
        note: "Scan from Slip",
        date: foundDate ? getCurrentDateTimeLocal(foundDate) : prev.date,
      }));
      setStatus("✅ อ่านข้อมูลแล้ว");
    } catch (err) {
      setStatus("❌ อ่านรูปไม่สำเร็จ");
    }
    setIsProcessingImg(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.assetId || !form.category) return alert("ข้อมูลไม่ครบ");
    setLoading(true);
    try {
      await fetch(apiUrl, {
        method: "POST",
        body: JSON.stringify({ action: "addTransaction", ...form }),
      });
      setStatus("🎉 บันทึกสำเร็จ!");

      const defaultCat = appCategories[form.type]?.[0]?.label || "";
      setForm((prev) => ({
        ...prev,
        amount: "",
        note: "",
        category: defaultCat,
        date: getCurrentDateTimeLocal(),
      }));

      setPreview(null);
      setShowRecordModal(false);
      refreshData();
      setTimeout(() => setStatus(""), 2000);
    } catch (error) {
      setStatus("❌ Error");
    }
    setLoading(false);
  };

  const handleUpdateTransaction = async () => {
    if (!editingTransaction) return;
    setLoading(true);
    try {
      await fetch(apiUrl, {
        method: "POST",
        body: JSON.stringify({
          action: "updateTransaction",
          id: editingTransaction.ID,
          date: editingTransaction.date,
          type: editingTransaction.Type,
          category: editingTransaction.Category,
          amount: editingTransaction.Amount,
          note: editingTransaction.Note,
          assetId: editingTransaction.Asset_ID,
        }),
      });
      setStatus("✅ แก้ไขเรียบร้อย");
      setEditingTransaction(null);
      refreshData();
    } catch (error) {
      setStatus("❌ แก้ไขไม่สำเร็จ");
    }
    setLoading(false);
  };

  const handleDeleteTransaction = async () => {
    if (!editingTransaction || !window.confirm("ต้องการลบรายการนี้ใช่ไหม?"))
      return;
    setLoading(true);
    try {
      await fetch(apiUrl, {
        method: "POST",
        body: JSON.stringify({
          action: "deleteTransaction",
          id: editingTransaction.ID,
        }),
      });
      setStatus("🗑️ ลบเรียบร้อย");
      setEditingTransaction(null);
      refreshData();
    } catch (error) {
      setStatus("❌ ลบไม่สำเร็จ");
    }
    setLoading(false);
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(apiUrl, {
        method: "POST",
        body: JSON.stringify({ action: "addAsset", ...newAssetForm }),
      });
      setIsCreatingAsset(false);
      refreshData();
    } catch (err) {}
    setLoading(false);
  };

  const handleUpdateAsset = async () => {
    if (!editingAsset) return;
    setLoading(true);
    try {
      await fetch(apiUrl, {
        method: "POST",
        body: JSON.stringify({
          action: "updateAsset",
          id: editingAsset.ID,
          value: editingAsset.Current_Value,
          date: editingAsset.date || getCurrentDateTimeLocal(),
        }),
      });
      setEditingAsset(null);
      refreshData();
    } catch (err) {}
    setLoading(false);
  };

  if (isAuthChecking)
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-emerald-500 font-bold">
        Loading...
      </div>
    );
  if (!isAuthenticated) return <Login onLogin={handleLoginSuccess} />;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans relative overflow-hidden selection:bg-emerald-500/30">
      <div className="max-w-7xl mx-auto p-4 md:p-6 w-full space-y-8 pb-32">
        <header className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3 self-start md:self-auto">
            <div className="bg-emerald-600 p-2 rounded-xl shadow-lg shadow-emerald-500/20">
              <IconWallet className="text-white w-6 h-6" />
            </div>
            <h1 className="text-2xl font-bold text-white">SpendWise</h1>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === "dashboard" && (
              <div className="flex items-center gap-2 bg-slate-900 p-1 rounded-xl border border-slate-800 self-end md:self-auto">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="px-3 py-1.5 text-xs font-bold bg-slate-800 rounded-lg text-slate-300 outline-none border-none cursor-pointer hover:text-white"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
                <button
                  onClick={refreshData}
                  className="p-2 hover:bg-slate-800 rounded-lg text-slate-400"
                >
                  <IconRefresh size={16} />
                </button>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="p-2 bg-red-900/20 text-red-400 border border-red-900 rounded-xl hover:bg-red-900/40"
              title="Logout"
            >
              <IconLogOut size={16} />
            </button>
          </div>
        </header>

        <div className="pb-24">
          {activeTab === "dashboard" && (
            <div className="animate-fadeIn">
              <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
                {THAI_MONTHS_SHORT.map((m, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedMonth(i)}
                    className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all ${
                      selectedMonth === i
                        ? "bg-emerald-600 text-white shadow-lg shadow-emerald-600/30"
                        : "bg-slate-900 text-slate-400 border border-slate-800 hover:bg-slate-800"
                    }`}
                  >
                    {m === "ธ.ค." && selectedMonth !== i ? "ธ.ค." : m}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-4">
                <div className="bg-emerald-600 rounded-3xl p-6 shadow-2xl relative overflow-hidden">
                  <div className="absolute -right-4 -bottom-4 opacity-20 text-emerald-900">
                    <IconWallet size={120} />
                  </div>
                  <p className="text-emerald-100 text-sm font-medium mb-1">
                    รายจ่ายรวม (เดือนนี้)
                  </p>
                  <h2 className="text-3xl font-bold text-white mb-2">
                    ฿{dashboardData.totalExpense.toLocaleString()}
                  </h2>
                  <div className="inline-flex items-center gap-1 bg-emerald-500/30 px-2 py-1 rounded-lg text-xs text-white font-bold backdrop-blur-sm">
                    <span>
                      รายรับ: ฿{dashboardData.totalIncome.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="bg-slate-900 rounded-3xl p-6 border border-slate-800 shadow-xl relative">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-slate-400 text-sm font-medium">
                        หมวดหมู่สูงสุด
                      </p>
                      <h3 className="text-lg font-bold text-white truncate max-w-[120px]">
                        {dashboardData.topCatName}
                      </h3>
                    </div>
                    <div className="bg-blue-500/20 p-2 rounded-lg text-blue-400">
                      <IconHome size={20} />
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-200 mb-1">
                    ฿{dashboardData.topCatAmount.toLocaleString()}
                  </h2>
                </div>
                <div className="bg-slate-900 rounded-3xl p-6 border border-slate-800 shadow-xl border-l-4 border-l-amber-500">
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-slate-400 text-sm font-medium">
                      คาดการณ์สิ้นเดือน
                    </p>
                    <div className="bg-amber-500/20 p-2 rounded-lg text-amber-500">
                      <IconTarget size={20} />
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-200 mb-4">
                    ฿
                    {(dashboardData.totalExpense * 1.1).toLocaleString(
                      undefined,
                      { maximumFractionDigits: 0 }
                    )}
                  </h2>
                </div>
                <div
                  className={`rounded-3xl p-6 border shadow-xl ${
                    dashboardData.totalIncome - dashboardData.totalExpense >= 0
                      ? "bg-slate-900 border-slate-800"
                      : "bg-red-900/20 border-red-900/50"
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <p className="text-slate-400 text-sm font-medium">
                      เงินคงเหลือ (เดือนนี้)
                    </p>
                    <div className="bg-slate-800 p-2 rounded-lg text-slate-400">
                      <IconActivity size={20} />
                    </div>
                  </div>
                  <h2
                    className={`text-2xl font-bold mb-4 ${
                      dashboardData.totalIncome - dashboardData.totalExpense >=
                      0
                        ? "text-emerald-400"
                        : "text-red-400"
                    }`}
                  >
                    {dashboardData.totalIncome - dashboardData.totalExpense >= 0
                      ? "+"
                      : ""}{" "}
                    ฿
                    {(
                      dashboardData.totalIncome - dashboardData.totalExpense
                    ).toLocaleString()}
                  </h2>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
                <div className="lg:col-span-8 bg-slate-900 rounded-3xl p-6 border border-slate-800 shadow-xl">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <span className="w-1 h-6 bg-slate-700 rounded-full"></span>
                      {chartViewMode === "spending"
                        ? `Yearly Spending Trend (${selectedYear})`
                        : "Net Worth Trend (Last 6 Months)"}
                    </h3>
                    <div className="flex bg-slate-800 p-1 rounded-xl">
                      <button
                        onClick={() => setChartViewMode("spending")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition ${
                          chartViewMode === "spending"
                            ? "bg-emerald-600 text-white shadow"
                            : "text-slate-400 hover:text-white"
                        }`}
                      >
                        <IconBarChart size={14} /> รายรับ-รายจ่าย
                      </button>
                      <button
                        onClick={() => setChartViewMode("assets")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition ${
                          chartViewMode === "assets"
                            ? "bg-yellow-500 text-slate-900 shadow"
                            : "text-slate-400 hover:text-white"
                        }`}
                      >
                        <IconTrending size={14} /> สินทรัพย์
                      </button>
                    </div>
                  </div>
                  <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      {chartViewMode === "spending" ? (
                        <BarChart
                          data={dashboardData.spendingTrendData}
                          barSize={20}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#1e293b"
                            vertical={false}
                          />
                          <XAxis
                            dataKey="name"
                            stroke="#64748b"
                            tick={{ fontSize: 10 }}
                            axisLine={false}
                            tickLine={false}
                            dy={10}
                          />
                          <YAxis
                            stroke="#64748b"
                            tick={{ fontSize: 10 }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#0f172a",
                              borderColor: "#334155",
                              borderRadius: "12px",
                              color: "#fff",
                            }}
                            cursor={{ fill: "#1e293b" }}
                            formatter={(val) => val.toLocaleString()}
                          />
                          <Legend />
                          <Bar
                            dataKey="Income"
                            name="รายรับ"
                            stackId="a"
                            fill="#10B981"
                            radius={[0, 0, 4, 4]}
                          />
                          <Bar
                            dataKey="Expense"
                            name="รายจ่าย"
                            stackId="a"
                            fill="#3B82F6"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      ) : (
                        <LineChart data={dashboardData.assetTrendData}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#1e293b"
                            vertical={false}
                          />
                          <XAxis
                            dataKey="name"
                            stroke="#64748b"
                            tick={{ fontSize: 10 }}
                            axisLine={false}
                            tickLine={false}
                            dy={10}
                          />
                          <YAxis
                            stroke="#64748b"
                            tick={{ fontSize: 10 }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#0f172a",
                              borderColor: "#334155",
                              borderRadius: "12px",
                              color: "#fff",
                            }}
                            formatter={(val) => val.toLocaleString()}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="Assets"
                            name="สินทรัพย์"
                            stroke="#FACC15"
                            strokeWidth={3}
                            dot={{ r: 4 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="Liabilities"
                            name="หนี้สิน"
                            stroke="#EF4444"
                            strokeWidth={3}
                            dot={{ r: 4 }}
                          />
                          <Line
                            type="monotone"
                            dataKey="NetWorth"
                            name="ความมั่งคั่งสุทธิ"
                            stroke="#2DD4BF"
                            strokeWidth={2}
                            strokeDasharray="5 5"
                          />
                        </LineChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="lg:col-span-4 bg-slate-900 rounded-3xl p-6 border border-slate-800 shadow-xl flex flex-col">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        {chartViewMode === "spending"
                          ? "Monthly Categories"
                          : "Asset Allocation"}
                      </h3>
                      {chartViewMode === "assets" && (
                        <div className="flex gap-2 mt-2">
                          {["ALL", "LIQUID", "INVESTMENT"].map((mode) => (
                            <button
                              key={mode}
                              onClick={() => setAssetSubMode(mode)}
                              className={`text-[10px] px-2 py-1 rounded border ${
                                assetSubMode === mode
                                  ? "bg-slate-700 text-white border-slate-600"
                                  : "text-slate-500 border-slate-800 hover:border-slate-700"
                              }`}
                            >
                              {mode}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-h-[200px] relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={getDisplayDonutData()}
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {getDisplayDonutData().map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={
                                entry.color ||
                                CHART_COLORS[index % CHART_COLORS.length]
                              }
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#0f172a",
                            borderColor: "#334155",
                            borderRadius: "8px",
                            color: "#fff",
                          }}
                          itemStyle={{ color: "#fff" }}
                          formatter={(val) => `฿${val.toLocaleString()}`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-xs text-slate-500">TOTAL</span>
                      <span className="text-lg font-bold text-white">
                        ฿{getDisplayDonutTotal().toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2 mt-4 max-h-40 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700">
                    {getDisplayDonutData().map((d, i) => {
                      const total = getDisplayDonutTotal();
                      const percent =
                        total > 0
                          ? ((d.value / total) * 100).toFixed(1)
                          : "0.0";
                      return (
                        <div
                          key={i}
                          className="flex justify-between items-center text-xs"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{
                                backgroundColor:
                                  d.color ||
                                  CHART_COLORS[i % CHART_COLORS.length],
                              }}
                            ></span>
                            <span className="text-slate-300 truncate max-w-[100px]">
                              {d.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-slate-200">
                              ฿{d.value.toLocaleString()}
                            </span>
                            <span className="text-slate-500 w-10 text-right">
                              {percent}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="mb-8 mt-6">
                <h3 className="text-xl font-bold text-white mb-4">
                  Recent Transactions
                </h3>
                <div className="bg-slate-900 p-2 rounded-2xl border border-slate-800 mb-4 flex flex-col md:flex-row gap-2">
                  <div className="relative flex-1">
                    <IconSearch className="absolute left-3 top-3 text-slate-500 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search..."
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:border-emerald-500 outline-none transition"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </div>
                  <div className="relative">
                    <select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="w-full md:w-48 bg-slate-950 border border-slate-800 text-slate-300 text-sm rounded-xl px-4 py-2.5 outline-none appearance-none cursor-pointer hover:border-emerald-500/50 transition"
                    >
                      {allCategories.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    <IconChevronDown className="absolute right-3 top-3 text-slate-500 w-4 h-4 pointer-events-none" />
                  </div>
                  <div className="relative">
                    <select
                      value={filterDateRange}
                      onChange={(e) => setFilterDateRange(e.target.value)}
                      className="w-full md:w-40 bg-slate-950 border border-slate-800 text-slate-300 text-sm rounded-xl px-4 py-2.5 outline-none appearance-none cursor-pointer"
                    >
                      <option>All Time</option>
                      <option>1 Day</option>
                      <option>1 Week</option>
                      <option>This Month</option>
                      <option>Last Month</option>
                    </select>
                    <IconClock className="absolute right-3 top-3 text-slate-500 w-4 h-4 pointer-events-none" />
                  </div>
                </div>
                <div className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden">
                  <div className="divide-y divide-slate-800">
                    {filteredTransactions.slice(0, 10).map((t, i) => {
                      const catInfo = getCatInfo(t.Category);

                      return (
                        <div
                          key={t.ID}
                          onClick={() =>
                            setEditingTransaction({
                              ...t,
                              date: getCurrentDateTimeLocal(
                                parseDateSafe(t.Date)
                              ),
                            })
                          }
                          className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-slate-800/50 transition cursor-pointer active:bg-slate-800"
                        >
                          <div className="col-span-6 md:col-span-5 flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-lg shadow-inner border border-slate-700"
                              style={
                                t.Type === "Expense"
                                  ? {
                                      backgroundColor: `${catInfo.color}15`,
                                      color: catInfo.color,
                                    }
                                  : {
                                      backgroundColor:
                                        "rgba(104, 211, 145, 0.15)",
                                      color: "#68D391",
                                    }
                              }
                            >
                              {t.Type === "Expense" ? catInfo.emoji : "💰"}
                            </div>
                            <span className="font-bold text-slate-200 text-sm truncate">
                              {t.Note || "รายการทั่วไป"}
                            </span>
                          </div>
                          <div
                            className="col-span-3 hidden md:block text-sm"
                            style={{
                              color:
                                t.Type === "Expense"
                                  ? catInfo.color
                                  : "#68D391",
                            }}
                          >
                            {t.Category || "-"}
                          </div>
                          <div className="col-span-3 md:col-span-2 text-slate-500 text-sm">
                            {formatThaiDate(t.Date)}
                          </div>
                          <div
                            className={`col-span-3 md:col-span-2 text-right font-bold text-sm ${
                              t.Type === "Expense"
                                ? "text-slate-200"
                                : "text-emerald-400"
                            }`}
                          >
                            {t.Type === "Expense" ? "-" : "+"} ฿
                            {parseFloat(t.Amount).toLocaleString()}
                          </div>
                        </div>
                      );
                    })}
                    {filteredTransactions.length === 0 && (
                      <div className="text-center py-10 text-slate-600">
                        ไม่พบรายการที่ค้นหา
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-24 animate-fadeIn">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-white">My Assets</h3>
                  <button
                    onClick={() => setIsCreatingAsset(true)}
                    className="text-xs bg-slate-800 text-emerald-400 px-3 py-1.5 rounded-full border border-slate-700 hover:bg-slate-700"
                  >
                    + Add New
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.values(ASSET_GROUPS).map((group) => {
                    const groupAssets = assets.filter(
                      (a) => getAssetGroupType(a.Type).id === group.id
                    );
                    const total = groupAssets.reduce(
                      (sum, a) => sum + (parseFloat(a.Current_Value) || 0),
                      0
                    );
                    return (
                      <div
                        key={group.id}
                        className="bg-slate-900 rounded-2xl border border-slate-800 p-4"
                      >
                        <div className="flex justify-between items-center mb-3">
                          <h4
                            className="font-bold text-sm"
                            style={{ color: group.color }}
                          >
                            {group.title}
                          </h4>
                          <span className="text-white font-bold text-sm">
                            ฿{total.toLocaleString()}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {groupAssets.length > 0 ? (
                            groupAssets.map((asset) => (
                              <div
                                key={asset.ID}
                                onClick={() =>
                                  setEditingAsset({
                                    ...asset,
                                    date: getCurrentDateTimeLocal(),
                                  })
                                }
                                className="flex justify-between text-xs text-slate-400 hover:bg-slate-800 p-2 rounded-lg cursor-pointer transition"
                              >
                                <span>{asset.Asset_Name}</span>
                                <span className="text-slate-200">
                                  ฿
                                  {parseFloat(
                                    asset.Current_Value
                                  ).toLocaleString()}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="text-xs text-slate-600 text-center py-2">
                              ไม่มีรายการ
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* TAB 2, 3, 4: แท็บอื่นๆ ที่สร้างไฟล์แยกไว้ */}
          {/* ========================================================= */}
          {activeTab === "planner" && <PlannerPage apiUrl={apiUrl} />}
          {activeTab === "investment" && <InvestmentPage apiUrl={apiUrl} />}
          {activeTab === "goals" && <GoalPage apiUrl={apiUrl} />}
          {activeTab === "dca" && <DCAPage apiUrl={apiUrl} />}
        </div>

        {/* --- BOTTOM NAVIGATION BAR --- */}
        <div className="fixed bottom-0 left-0 w-full bg-slate-950/90 backdrop-blur-xl border-t border-slate-800 p-3 flex justify-around items-center z-50 safe-area-pb">
          <button
            onClick={() => setActiveTab("dashboard")}
            className={`flex flex-col items-center gap-1 transition-all ${
              activeTab === "dashboard"
                ? "text-emerald-400 scale-110"
                : "text-slate-500 hover:text-emerald-200"
            }`}
          >
            <IconHome size={22} />
            <span className="text-[9px] font-bold">ภาพรวม</span>
          </button>

          <button
            onClick={() => setActiveTab("planner")}
            className={`flex flex-col items-center gap-1 transition-all ${
              activeTab === "planner"
                ? "text-indigo-400 scale-110"
                : "text-slate-500 hover:text-indigo-200"
            }`}
          >
            <IconCalendar size={22} />
            <span className="text-[9px] font-bold">แผนรายจ่าย</span>
          </button>

          {/* ปุ่มบันทึกรายการตรงกลาง โชว์เฉพาะตอนอยู่หน้า Dashboard */}
          <div className="w-12 h-12 flex items-center justify-center relative -top-5">
            {activeTab === "dashboard" && (
              <button
                onClick={() => setShowRecordModal(true)}
                className="absolute w-12 h-12 bg-emerald-600 hover:bg-emerald-500 text-white rounded-full shadow-lg shadow-emerald-500/40 flex items-center justify-center border-4 border-slate-950 transition-all active:scale-90"
              >
                <IconPlus size={24} />
              </button>
            )}
          </div>

          <button
            onClick={() => setActiveTab("goals")}
            className={`flex flex-col items-center gap-1 transition-all ${
              activeTab === "goals"
                ? "text-yellow-400 scale-110"
                : "text-slate-500 hover:text-yellow-200"
            }`}
          >
            <IconFlag size={22} />
            <span className="text-[9px] font-bold">เป้าหมาย</span>
          </button>

          <button
            onClick={() => setActiveTab("investment")}
            className={`flex flex-col items-center gap-1 transition-all ${
              activeTab === "investment"
                ? "text-cyan-400 scale-110"
                : "text-slate-500 hover:text-cyan-200"
            }`}
          >
            <IconTrending size={22} />
            <span className="text-[9px] font-bold">ลงทุน</span>
          </button>

          <button
            onClick={() => setActiveTab("dca")}
            className={`flex flex-col items-center gap-1 transition-all ${
              activeTab === "dca"
                ? "text-amber-400 scale-110"
                : "text-slate-500 hover:text-amber-200"
            }`}
          >
            <span
              className={`text-xl font-black leading-none ${
                activeTab === "dca" ? "text-amber-400" : "text-slate-500"
              }`}
            >
              ₿
            </span>
            <span className="text-[9px] font-bold">DCA</span>
          </button>
        </div>

        {/* --- MODALS สำหรับหน้า DASHBOARD --- */}
        {showRecordModal && (
          <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 w-full max-w-md rounded-3xl border border-slate-700 shadow-2xl p-6 relative animate-fadeIn max-h-[90vh] overflow-y-auto">
              <button
                onClick={() => setShowRecordModal(false)}
                className="absolute top-4 right-4 text-slate-400 hover:text-white"
              >
                <IconX />
              </button>
              <h2 className="text-xl font-bold text-white mb-6">
                บันทึกรายการ
              </h2>
              {preview && (
                <img
                  src={preview}
                  className="w-full h-32 object-cover rounded-xl mb-4 border border-slate-700"
                  alt="slip"
                />
              )}
              <label className="flex gap-2 w-full bg-slate-800 border border-dashed border-slate-600 text-slate-400 rounded-xl p-4 cursor-pointer hover:bg-slate-700 mb-4 justify-center">
                <IconCamera />
                <span className="text-sm">สแกนสลิป</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onClick={(e) => (e.target.value = null)}
                  onChange={handleImageUpload}
                />
              </label>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex bg-slate-950 p-1 rounded-xl border border-slate-800">
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        type: "Expense",
                        category: appCategories.Expense[0]?.label || "",
                      })
                    }
                    className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${
                      form.type === "Expense"
                        ? "bg-red-500 text-white"
                        : "text-slate-500"
                    }`}
                  >
                    รายจ่าย
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        type: "Income",
                        category: appCategories.Income[0]?.label || "",
                      })
                    }
                    className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${
                      form.type === "Income"
                        ? "bg-emerald-500 text-white"
                        : "text-slate-500"
                    }`}
                  >
                    รายรับ
                  </button>
                </div>
                <div>
                  <label className="text-xs text-slate-500 ml-2">ยอดเงิน</label>
                  <input
                    type="number"
                    required
                    value={form.amount}
                    onChange={(e) =>
                      setForm({ ...form, amount: e.target.value })
                    }
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-center text-2xl font-bold outline-none focus:border-emerald-500"
                    placeholder="0.00"
                  />
                </div>

                {/* 🟢 แยกวันที่และเวลาออกจากกัน (หน้าเพิ่มรายการ) */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 ml-2">
                      วันที่
                    </label>
                    <input
                      type="date"
                      required
                      value={form.date.split("T")[0]}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          date: `${e.target.value}T${
                            form.date.split("T")[1] || "00:00"
                          }`,
                        })
                      }
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-xs outline-none focus:border-emerald-500 scheme-dark"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 ml-2">
                      เวลา (ชั่วโมง:นาที)
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="08:30"
                      maxLength="5"
                      value={(form.date.split("T")[1] || "").substring(0, 5)}
                      onChange={(e) => {
                        const newTime = formatTimeInput(e.target.value);
                        setForm({
                          ...form,
                          date: `${form.date.split("T")[0]}T${newTime}`,
                        });
                      }}
                      onBlur={(e) =>
                        handleTimeBlur(e.target.value, form.date, (newDate) =>
                          setForm({ ...form, date: newDate })
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-xs outline-none focus:border-emerald-500 text-center font-bold tracking-widest placeholder:font-normal placeholder:tracking-normal placeholder:text-slate-700"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-500 ml-2 mb-2 block">
                    หมวดหมู่
                  </label>
                  <CategorySelector
                    type={form.type}
                    selectedCategory={form.category}
                    onSelect={(val) => setForm({ ...form, category: val })}
                    appCategories={appCategories}
                    setAppCategories={setAppCategories}
                  />
                </div>

                <div>
                  <label className="text-xs text-slate-500 ml-2">บัญชี</label>
                  <select
                    value={form.assetId}
                    onChange={(e) =>
                      setForm({ ...form, assetId: e.target.value })
                    }
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white outline-none"
                  >
                    {Object.values(ASSET_GROUPS).map((g) => (
                      <optgroup key={g.id} label={g.title}>
                        {assets
                          .filter((a) => getAssetGroupType(a.Type).id === g.id)
                          .map((a) => (
                            <option key={a.ID} value={a.ID}>
                              {a.Asset_Name}
                            </option>
                          ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <input
                  type="text"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white outline-none focus:border-emerald-500"
                  placeholder="บันทึกช่วยจำ..."
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3.5 rounded-xl shadow-lg transition"
                >
                  บันทึก
                </button>
              </form>
            </div>
          </div>
        )}

        {editingTransaction && (
          <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
            <div className="bg-slate-900 w-full max-w-md p-6 rounded-3xl border border-slate-700 shadow-2xl animate-fadeIn max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-white">แก้ไขรายการ</h3>
                <button
                  onClick={() => setEditingTransaction(null)}
                  className="text-slate-400 hover:text-white"
                >
                  <IconX />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex bg-slate-950 p-1 rounded-xl border border-slate-800">
                  <button
                    onClick={() =>
                      setEditingTransaction({
                        ...editingTransaction,
                        Type: "Expense",
                        Category: appCategories.Expense[0]?.label || "",
                      })
                    }
                    className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${
                      editingTransaction.Type === "Expense"
                        ? "bg-red-500 text-white"
                        : "text-slate-500"
                    }`}
                  >
                    รายจ่าย
                  </button>
                  <button
                    onClick={() =>
                      setEditingTransaction({
                        ...editingTransaction,
                        Type: "Income",
                        Category: appCategories.Income[0]?.label || "",
                      })
                    }
                    className={`flex-1 py-2 rounded-lg text-sm font-bold transition ${
                      editingTransaction.Type === "Income"
                        ? "bg-emerald-500 text-white"
                        : "text-slate-500"
                    }`}
                  >
                    รายรับ
                  </button>
                </div>

                <input
                  type="number"
                  value={editingTransaction.Amount}
                  onChange={(e) =>
                    setEditingTransaction({
                      ...editingTransaction,
                      Amount: e.target.value,
                    })
                  }
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-center text-2xl font-bold outline-none"
                />

                {/* 🟢 แยกวันที่และเวลาออกจากกัน (หน้าแก้ไขรายการ) */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 ml-2">
                      วันที่
                    </label>
                    <input
                      type="date"
                      required
                      value={editingTransaction.date.split("T")[0]}
                      onChange={(e) =>
                        setEditingTransaction({
                          ...editingTransaction,
                          date: `${e.target.value}T${
                            editingTransaction.date.split("T")[1] || "00:00"
                          }`,
                        })
                      }
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-xs outline-none focus:border-emerald-500 scheme-dark"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 ml-2">
                      เวลา (ชั่วโมง:นาที)
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="08:30"
                      maxLength="5"
                      value={(
                        editingTransaction.date.split("T")[1] || ""
                      ).substring(0, 5)}
                      onChange={(e) => {
                        const newTime = formatTimeInput(e.target.value);
                        setEditingTransaction({
                          ...editingTransaction,
                          date: `${
                            editingTransaction.date.split("T")[0]
                          }T${newTime}`,
                        });
                      }}
                      onBlur={(e) =>
                        handleTimeBlur(
                          e.target.value,
                          editingTransaction.date,
                          (newDate) =>
                            setEditingTransaction({
                              ...editingTransaction,
                              date: newDate,
                            })
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white text-xs outline-none focus:border-emerald-500 text-center font-bold tracking-widest placeholder:font-normal placeholder:tracking-normal placeholder:text-slate-700"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-500 ml-2 mb-2 block">
                    หมวดหมู่
                  </label>
                  <CategorySelector
                    type={editingTransaction.Type}
                    selectedCategory={editingTransaction.Category}
                    onSelect={(val) =>
                      setEditingTransaction({
                        ...editingTransaction,
                        Category: val,
                      })
                    }
                    appCategories={appCategories}
                    setAppCategories={setAppCategories}
                  />
                </div>

                <select
                  value={editingTransaction.Asset_ID}
                  onChange={(e) =>
                    setEditingTransaction({
                      ...editingTransaction,
                      Asset_ID: e.target.value,
                    })
                  }
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white outline-none"
                >
                  {Object.values(ASSET_GROUPS).map((g) => (
                    <optgroup key={g.id} label={g.title}>
                      {assets
                        .filter((a) => getAssetGroupType(a.Type).id === g.id)
                        .map((a) => (
                          <option key={a.ID} value={a.ID}>
                            {a.Asset_Name}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </select>
                <input
                  type="text"
                  value={editingTransaction.Note}
                  onChange={(e) =>
                    setEditingTransaction({
                      ...editingTransaction,
                      Note: e.target.value,
                    })
                  }
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white"
                  placeholder="บันทึกช่วยจำ..."
                />

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleDeleteTransaction}
                    className="flex-1 bg-red-900/50 text-red-400 border border-red-900 py-3 rounded-xl font-bold flex items-center justify-center gap-2"
                  >
                    <IconTrash size={16} /> ลบ
                  </button>
                  <button
                    onClick={handleUpdateTransaction}
                    className="flex-[2] bg-emerald-600 text-white py-3 rounded-xl font-bold"
                  >
                    บันทึกการแก้ไข
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {(isCreatingAsset || editingAsset) && (
          <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md z-[60] flex items-center justify-center p-4">
            <div className="bg-slate-900 w-full max-w-sm rounded-3xl border border-slate-700 p-6 animate-fadeIn">
              <h3 className="font-bold text-white mb-2 text-center text-yellow-500">
                {isCreatingAsset ? "เพิ่มบัญชีใหม่" : "อัปเดตมูลค่า"}
              </h3>
              {editingAsset && (
                <p className="text-center text-slate-400 mb-6 font-bold text-lg">
                  {editingAsset.Asset_Name}
                </p>
              )}
              <div className="space-y-4">
                {isCreatingAsset && (
                  <>
                    <input
                      type="text"
                      placeholder="ชื่อบัญชี"
                      className="w-full bg-slate-950 p-3 rounded-xl border border-slate-800 text-white outline-none"
                      value={newAssetForm.name}
                      onChange={(e) =>
                        setNewAssetForm({
                          ...newAssetForm,
                          name: e.target.value,
                        })
                      }
                    />
                    <select
                      className="w-full bg-slate-950 p-3 rounded-xl border border-slate-800 text-white outline-none"
                      value={newAssetForm.type}
                      onChange={(e) =>
                        setNewAssetForm({
                          ...newAssetForm,
                          type: e.target.value,
                        })
                      }
                    >
                      {Object.values(ASSET_GROUPS).map((g) => (
                        <optgroup key={g.id} label={g.title}>
                          {g.types.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </>
                )}
                {!isCreatingAsset && (
                  <div>
                    <label className="text-xs text-slate-500 ml-2">
                      วันที่ของมูลค่านี้
                    </label>
                    <input
                      type="datetime-local"
                      className="w-full bg-slate-950 p-3 rounded-xl border border-slate-800 text-white outline-none scheme-dark"
                      value={editingAsset.date}
                      onChange={(e) =>
                        setEditingAsset({
                          ...editingAsset,
                          date: e.target.value,
                        })
                      }
                    />
                  </div>
                )}
                <div>
                  <label className="text-xs text-slate-500 ml-2">
                    มูลค่าปัจจุบัน (บาท)
                  </label>
                  <input
                    type="number"
                    placeholder="ยอดเงิน"
                    className="w-full bg-slate-950 p-3 rounded-xl border border-slate-800 text-white text-center font-bold text-3xl outline-none"
                    value={
                      isCreatingAsset
                        ? newAssetForm.value
                        : editingAsset.Current_Value
                    }
                    onChange={(e) =>
                      isCreatingAsset
                        ? setNewAssetForm({
                            ...newAssetForm,
                            value: e.target.value,
                          })
                        : setEditingAsset({
                            ...editingAsset,
                            Current_Value: e.target.value,
                          })
                    }
                  />
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => {
                      setIsCreatingAsset(false);
                      setEditingAsset(null);
                    }}
                    className="flex-1 bg-slate-800 text-slate-300 py-3 rounded-xl font-bold"
                  >
                    ยกเลิก
                  </button>
                  <button
                    onClick={
                      isCreatingAsset ? handleCreateAsset : handleUpdateAsset
                    }
                    className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-slate-900 py-3 rounded-xl font-bold"
                  >
                    บันทึก
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {status && (
          <div className="fixed top-6 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-6 py-2 rounded-full shadow-xl z-[70] animate-bounce text-sm font-bold">
            {status}
          </div>
        )}
        {loading && (
          <div className="fixed inset-0 bg-slate-950/80 z-[70] flex items-center justify-center">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>
    </div>
  );
}
